import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../redux/store';
import { BookAuthorType } from '../../../types/Library';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { BookAuthorValidation } from './BookAuthorValidation';
import { useGetBooksQuery } from '../../../hooks/library/useGetBooksQuery';
import { useGetAuthorsQuery } from '../../../hooks/library/useGetAuthorsQuery';
import { useGetBookAuthorsQuery } from '../../../hooks/library/useGetBookAuthorsQuery';
import { useMutation, useQueryClient } from 'react-query';
import { createBookAuthor, updateBookAuthor } from '../../../services/library/bookAuthor';
import { key_bookAuthors } from '../../../utils/constants';
import toast from 'react-hot-toast';
import { ChevronLeftIcon, PlusIcon, TrashIcon } from '@radix-ui/react-icons';
import { Lists } from '../../../components/common/Lists';
import TableComponent from '../../../components/common/Tables';
import { ConfirmAlertDialog } from '../../../components/common/ConfirmAlertDialog';
import { Card, Flex, Button, Spinner } from '@radix-ui/themes';
import SelectComponent from '../../../components/inputs/Select/SelectComponent';
import { routes } from '../../../routes';
import { resetPreviousPath } from '../../../redux/slices/previousPath';
import { useAppDispatch } from '../../../hooks/hooks';
import { resetAuthorData } from '../../../redux/slices/library/author';
import { resetBookData } from '../../../redux/slices/library/book';


const columnNamesMap: { [key: string]: string } = {
    bookTitle: 'Livro',
    authorName: 'Autor',
};

export const ManageBookAuthors = () => {
    const [currentPage, setCurrentPage] = useState(1);
    const navigate = useNavigate();
    const dispatch = useAppDispatch();

    const queryClient = useQueryClient();
    const previousPath = useSelector((state: RootState) => state.previousPath);

    const mutationCreate = useMutation({ mutationFn: createBookAuthor, mutationKey: "createBookAuthor" });
    const mutationUpdate = useMutation(updateBookAuthor, {
        onSuccess: () => {
            queryClient.invalidateQueries(key_bookAuthors);
            toast.success('Apagado');
        },
        onError: () => {
            toast.error("Erro ao apagar.")
        }
    });
    const loadingState = mutationCreate.isLoading || mutationUpdate.isLoading;
    const [deleteItem, setDeleteItem] = useState<BookAuthorType | null>(null);
    const handleDelete = (c: BookAuthorType) => {
        c.status = false;
        mutationUpdate.mutateAsync(c)
    };
    const openDeleteDialog = (s: BookAuthorType) => {
        setDeleteItem(s);
    };
    const confirmDelete = async () => {
        if (deleteItem) {
            await handleDelete(deleteItem!)
        }
    }
    const bookData = useSelector((state: RootState) => state.book);
    const authorData = useSelector((state: RootState) => state.author);
    const defaultValues: BookAuthorType = {
        idBook: bookData?.id ?? '',
        idAuthor: authorData?.id ?? ''
    }

    const { control, handleSubmit, formState: { errors }, setValue } = useForm<BookAuthorType>({
        resolver: yupResolver(BookAuthorValidation),
        defaultValues,
        mode: 'onBlur',
        reValidateMode: 'onBlur',
        shouldUseNativeValidation: false,
    });

    const { data: booksData, status: statusBooks, isLoading: isLoadingBooks, isError:isErrorBooks, refetch: refreshBooks } = useGetBooksQuery({
        PageSize: 4000,
        PageNumber: 1,
    });
    const { data: authorsData, status: statusAuthors, isLoading: isLoadingAuthors, isError: isErrorAuthors,  refetch: refreshAuthors } = useGetAuthorsQuery({
        PageSize: 4000,
        PageNumber: 1,
    });

    const { data: booksAuthorsData, status: statusBooksAuthors, isLoading: isLoadingBooksAuthors, refetch: refreshBooksAuthors } =
        useGetBookAuthorsQuery({
            PageSize: 15,
            PageNumber: currentPage,
            IdBook: bookData?.id,
            IdAuthor: authorData?.id,
        });

    const options = booksAuthorsData?.items.map((c: BookAuthorType) => [
        {
            label: 'Eliminar',
            onClick: () => openDeleteDialog(c),
            icon: <TrashIcon />
        }
    ]) || [];
    const handlePageChange = (page: number) => {
        setCurrentPage(page);
    };
    const onSubmit = handleSubmit(async (data) => {
        try {
            const result = await mutationCreate.mutateAsync(data);
            if (result) {
                refreshBooksAuthors();
            }
        } catch (err: any) {
            toast.error(`${err?.message}`);
        }
    })
    const handleBack = () => {
        const path = previousPath.previousPath!
        dispatch(resetPreviousPath())
        dispatch(resetAuthorData())
        dispatch(resetBookData())
        navigate(path)
      }

      useEffect(()=>{
        if(!authorData?.id && !bookData?.id){
            handleBack();
          }
      },[authorData?.id, bookData?.id])

    
    return (
        <Card size='3'>
            <Flex direction="row" gap='6'>
                <Flex direction="column" gap="5" style={{ flex: 1 }}>
                    <form onSubmit={onSubmit} noValidate>
                        <Flex direction="column" gap='4'>
                            <SelectComponent
                                label="Autor"
                                options={authorsData?.items.map((author) => ({
                                    value: author?.id ?? "",
                                    label: `${author?.fullname}`
                                }))}
                                errors={errors}
                                control={control}
                                name="idAuthor"
                                itemsLoading ={isLoadingAuthors}
                                itemsError = {isErrorAuthors}
                           //     fieldProps={{ disabled: !!!authorData?.id }}
                                required={true}
                            />

                            <SelectComponent
                                label="Livro"
                                options={booksData?.items.map((book) => ({
                                    value: book?.id ?? "",
                                    label: `${book?.title}`
                                }))}
                                errors={errors}
                                control={control}
                                name="idBook"
                                itemsLoading ={isLoadingBooks}
                                itemsError = {isErrorBooks}
                            //    fieldProps={{ disabled: !!!bookData?.id }}
                                required={true}
                            />
                            <Flex direction="row" gap="4" style={{ alignSelf: "end" }}>
                                <Button
                                    type="button"
                                    variant="soft"
                                    color="gray"
                                    onClick={handleBack}
                                    disabled={loadingState}
                                >
                                    <ChevronLeftIcon /> Sair
                                </Button>
                                <Button type="submit" disabled={loadingState}>
                                    {loadingState ? <Spinner loading /> : <PlusIcon />}
                                    Adicionar
                                </Button>
                            </Flex>

                        </Flex>
                    </form>
                </Flex>
                <Flex direction="column" gap="4" style={{ flex: 1 }}>
                    <>
                        <Lists
                            status={statusBooksAuthors}
                            TableComponent={() =>
                                <TableComponent
                                    columns={Object.keys(columnNamesMap)}
                                    items={booksAuthorsData?.items}
                                    options={options}
                                    columnNamesMap={columnNamesMap}
                                    totalPages={booksAuthorsData?.totalPages || 1}
                                    currentPage={currentPage}
                                    onPageChange={handlePageChange}
                                    emptyMessage="Ainda não há autores associados a este livro."
                                />
                            }
                        />
                        <ConfirmAlertDialog
                            open={!!deleteItem}
                            onOpenChange={(open) => !open && setDeleteItem(null)}
                            title="Esta operação é irreversível"
                            description="A associação entre o livro e o autor será removida. Pretende continuar?"
                            confirmLabel="Eliminar"
                            item={deleteItem}
                            onConfirm={confirmDelete}
                            onCancel={() => setDeleteItem(null)}
                        />
                    </>
                </Flex>
            </Flex>
        </Card>
    )

}
